/**
 * Demo dataset used by every page during the foundation sprint.
 *
 * The shape is intentionally realistic so future modules (scheduler, scoring,
 * comms) can be developed against meaningful sample data without rewriting
 * fixtures. It is *not* meant to ship in production builds — the data layer
 * will be replaced by D1/KV when the real persistence module lands.
 */

import type { AppDatabase } from '../services/store';
import { DEFAULT_LEAGUE } from '../models/league';

const t = (s: string): string => new Date(s).toISOString();

const meta = (id: string, ts = '2026-01-15T12:00:00Z') => ({
  id,
  createdAt: t(ts),
  updatedAt: t(ts),
});

export const winter2026SeedData: AppDatabase = {
  league: { ...DEFAULT_LEAGUE },
  profiles: [
    {
      ...meta('user-marcello'),
      kind: 'profile',
      displayName: 'League Admin',
      email: '',
      defaultRole: 'admin',
      globalRoles: ['admin'],
      pronouns: '',
      bio: '',
      directoryStatus: 'active',
      preferredContactMethod: 'email',
      contactPreferences: {},
      attributes: { roleContext: 'League owner' },
      source: 'demo',
      tags: ['admin'],
    },
  ],
  tournamentTypes: [
    {
      ...meta('type-ironman'),
      kind: 'tournament-type',
      typeId: 'ironman',
      label: 'Ironman Roller Hockey',
      description:
        'Single short games (typically 12 minutes). Goals + optional assists. Penalties are penalty-shots, not minutes.',
      defaults: {
        scoring: {
          trackAssists: true,
          trackPenaltyMinutes: false,
          trackPenaltyShotsOnly: true,
          trackGoalTimestamps: false,
          gameLengthMinutes: 12,
          tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head'] as const,
        },
        scheduling: {
          minTeamRestMinutes: 20,
          rinkTurnoverMinutes: 5,
          balanceBackToBacks: true,
        },
        modules: { proShop: false, concessions: false, locker: true, gamification: false },
      },
    },
    {
      ...meta('type-roller-season'),
      kind: 'tournament-type',
      typeId: 'roller-season',
      label: 'Roller Hockey Season',
      description:
        'Recurring roller hockey league play with weekly game nights, standings, penalties, and optional playoffs.',
      defaults: {
        scoring: {
          trackAssists: true,
          trackPenaltyMinutes: true,
          trackPenaltyShotsOnly: false,
          trackGoalTimestamps: true,
          gameLengthMinutes: 36,
          tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head', 'fewest_pims'] as const,
        },
        scheduling: {
          minTeamRestMinutes: 45,
          rinkTurnoverMinutes: 10,
          balanceBackToBacks: true,
        },
        modules: { proShop: false, concessions: false, locker: true, gamification: true },
        season: {
          weekdays: [] as const,
          firstPuckTime: '19:00',
          gamesPerRinkPerNight: 3,
          blackoutDates: [],
          rosterIntake: 'later',
          registration: {
            enabled: false,
            intakeType: 'individual',
            feeCents: 5000,
            requireWaiver: true,
            autoApprove: false,
          },
          halfSplit: false,
        },
        playoffs: {
          format: 'single_elim',
          qualifyingTeams: 4,
          seedingSource: 'final_standings',
        },
      },
    },
  ],
  tournaments: [],
  divisions: [],
  teams: [],
  players: [],
  rosterEntries: [],
  roleAssignments: [],
  gameAssignments: [],
  venues: [],
  rinks: [],
  scheduleSlots: [],
  games: [],
  gameScoringSessions: [],
  scoreEvents: [],
  gameResults: [],
  gameArchives: [],
  standings: [],
  communications: [],
  communicationThreads: [],
  communicationThreadMessages: [],
  communicationDeliveries: [],
  communicationConsents: [],
  announcements: [],
  lockerRoomMessages: [],
  registrations: [],
  waiverDocuments: [],
  waivers: [],
  paymentIntents: [],
  gameRosters: [],
  playerEligibility: [],
  officials: [],
  officialAssignments: [],
};
